import React, { FC, useState } from 'react';
import {
  formatDurationHHmm,
  getElevationMeasurementUnit,
  getMeasurementUnit,
} from '../../../../../helpers/general.helpers';
import LogFocusBadge from './log-focus-badge.component';
import LogSessionDetail from './log-session-detail.component';
import { DaySlot } from '../log.helpers';

interface LogDayRowProps {
  slot: DaySlot;
}

const LogDayRow: FC<LogDayRowProps> = ({ slot }) => {
  const [expanded, setExpanded] = useState(false);
  const distanceUnit = getMeasurementUnit();
  const elevationUnit = getElevationMeasurementUnit();
  const { trainings, state } = slot;

  if (state === 'today' && trainings.length === 0) {
    return (
      <div className="log-day-wrap log-day-current">
        <div className="log-day-summary log-day-future">
          <div className="log-day-name">
            {slot.dayLabel}
            <span className="log-day-today-badge">Today</span>
          </div>
          <div />
          <div className="log-day-rest-label">—</div>
          <div />
          <div />
          <div />
          <div />
        </div>
      </div>
    );
  }

  if (state === 'rest' || state === 'future') {
    return (
      <div className="log-day-wrap">
        <div className={`log-day-summary ${state === 'rest' ? 'log-day-rest' : 'log-day-future'}`}>
          <div className="log-day-name">{slot.dayLabel}</div>
          <div />
          <div className="log-day-rest-label">{state === 'rest' ? 'Rest' : '—'}</div>
          <div />
          <div />
          <div />
          <div />
        </div>
      </div>
    );
  }

  const totalDuration = trainings.reduce((sum, t) => sum + (t.workoutData?.totalDurationComplete ?? 0), 0);
  const totalDistance = trainings.reduce((sum, t) => sum + (t.workoutData?.totalDistanceComplete ?? 0), 0);
  const totalAscent = trainings.reduce((sum, t) => sum + (t.workoutData?.totalElevationGain ?? 0), 0);

  return (
    <div className={`log-day-wrap ${expanded ? 'expanded' : ''} ${state === 'today' ? 'log-day-current' : ''}`}>
      <div
        className="log-day-summary"
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded((v) => !v)}
      >
        <div className="log-day-name">
          {slot.dayLabel}
          {state === 'today' && <span className="log-day-today-badge">Today</span>}
        </div>
        <div className="log-day-sess">
          <span className="log-sess-badge">{trainings.length}</span>
        </div>
        <div className="log-day-stats">
          {trainings.map((training) => (
            <span
              key={String(training.id)}
              className="log-workout-pill"
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((v) => !v);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.stopPropagation();
                  setExpanded((v) => !v);
                }
              }}
            >
              <span className="log-workout-title">{training.title}</span>
              <LogFocusBadge focus={training.focus} />
            </span>
          ))}
        </div>
        <div className="log-day-dur">{formatDurationHHmm(totalDuration)}</div>
        <div className="log-day-dist">
          {Math.round(totalDistance)}
          {distanceUnit}
        </div>
        <div className="log-day-asc">
          {Math.round(totalAscent)}
          {elevationUnit}
        </div>
        <div className="log-day-chev">{expanded ? '▴' : '▾'}</div>
      </div>
      {expanded && (
        <div className="log-day-detail">
          {trainings.map((training) => (
            <LogSessionDetail key={String(training.id)} training={training} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LogDayRow;
